import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "../config";
import SEO from "../components/SEO";
import { useBookingModal } from "../context/BookingModalContext";

const weddingSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Wedding Bar Hire",
  "provider": { "@id": "https://www.barspotltd.co.uk/#business" },
  "name": "Wedding Bar Hire",
  "description": "Bespoke mobile bar hire for weddings with Day Packages, Cash Bars, Tab Bars and All-Inclusive options. 80+ weddings served with 5-star ratings.",
  "areaServed": [
    { "@type": "City", "name": "Birmingham" },
    { "@type": "City", "name": "Cambridge" },
    { "@type": "AdministrativeArea", "name": "West Midlands" },
    { "@type": "AdministrativeArea", "name": "Worcestershire" },
    { "@type": "Country", "name": "United Kingdom" }
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Wedding Bar Packages",
    "itemListElement": [
      { "@type": "Offer", "name": "Day Package", "price": "15.50", "priceCurrency": "GBP", "description": "Welcome drink, table wine, toasting drinks" },
      { "@type": "Offer", "name": "Cash Bar", "price": "800", "priceCurrency": "GBP", "description": "Guests pay for own drinks, minimum spend £800" },
      { "@type": "Offer", "name": "All-Inclusive", "price": "5", "priceCurrency": "GBP", "description": "Unlimited drinks from £5 per person per hour" }
    ]
  }
};

const PackageCard = ({ title, price, features, onSelect }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="bg-[#fafafa] rounded-2xl p-8 text-center"
  >
    <h3 className="text-2xl mb-2">{title}</h3>
    <p className="text-3xl font-bold text-[#a5dae0] mb-6">{price}</p>
    <ul className="space-y-3 mb-8">
      {features.map((f, j) => (
        <li key={j} className="flex items-center gap-2 text-[#666666]">
          <Check size={18} className="text-[#a5dae0]" /> {f}
        </li>
      ))}
    </ul>
    <button
      onClick={onSelect}
      className="w-full py-3 rounded-full border-2 border-[#a5dae0] text-[#1a1a1a] font-semibold hover:bg-[#a5dae0] transition-all"
    >
      Select Package
    </button>
  </motion.div>
);

const WeddingPage = () => {
  const { openBookingModal } = useBookingModal();

  const dayPackageFeatures = ["Welcome drink", "½ bottle table wine pp", "Water & juice per table", "Toasting drink"];
  const cashBarFeatures = ["Guests pay for drinks", "Full menu available", "No upfront cost", "Quality service guaranteed"];
  const allInclusiveFeatures = ["Unlimited drinks", "Full bar or selection", "4hr minimum", "Most popular choice"];

  return (
    <div className="pt-20">
      <SEO
        title="Wedding Bar Hire | Packages from £5pp/hr | Bar Spot"
        description="Bespoke wedding bar hire with Day Packages, Cash Bars & All-Inclusive options. 80+ weddings, 5-star rated. Serving Birmingham, Midlands & Cambridge."
        canonical="/weddings"
        schema={weddingSchema}
      />
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.weddingToast} alt="Wedding bar hire setup with champagne toast celebration" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#fafafa]/95 via-[#fafafa]/80 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <p className="text-[#a5dae0] font-script text-2xl mb-4">Weddings</p>
            <h1 className="text-5xl md:text-6xl mb-6">Wedding Bar Hire</h1>
            <p className="text-lg text-[#666666] leading-relaxed mb-8">
              From intimate ceremonies to grand celebrations, we deliver a bespoke bar experience
              tailored to your wedding vision. Over 80 weddings served with 5-star ratings across
              Birmingham, the Midlands, Cambridge and beyond.
            </p>
            <button
              onClick={openBookingModal}
              data-testid="wedding-cta"
              className="bg-[#a5dae0] text-[#1a1a1a] px-8 py-4 rounded-full font-semibold hover:bg-[#8bc9d0] transition-all inline-flex items-center gap-2"
            >
              Get Wedding Quote <ArrowRight size={20} />
            </button>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-4xl mb-12 text-center">Wedding Bar Packages</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <PackageCard title="Day Package" price="£15.50pp" features={dayPackageFeatures} onSelect={openBookingModal} />
            <PackageCard title="Cash Bar" price="Min £800" features={cashBarFeatures} onSelect={openBookingModal} />
            <PackageCard title="All-Inclusive" price="From £5pp/hr" features={allInclusiveFeatures} onSelect={openBookingModal} />
          </div>
        </div>
      </section>

      {/* Cross-links to other services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[#555555] mb-6">We also provide mobile bar hire for other events</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/private-hire" className="px-6 py-3 rounded-full border-2 border-gray-200 text-[#444444] hover:border-[#a5dae0] hover:text-[#a5dae0] transition-all text-sm font-medium">Corporate & Private Events</Link>
            <Link to="/festivals" className="px-6 py-3 rounded-full border-2 border-gray-200 text-[#444444] hover:border-[#a5dae0] hover:text-[#a5dae0] transition-all text-sm font-medium">Festivals & Large Events</Link>
            <Link to="/menu" className="px-6 py-3 rounded-full border-2 border-gray-200 text-[#444444] hover:border-[#a5dae0] hover:text-[#a5dae0] transition-all text-sm font-medium">View Drinks Menu</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WeddingPage;
